import { useState, useMemo } from "react";
import { HiOutlineViewGrid, HiOutlineViewList } from "react-icons/hi";
import { Header, Loader } from "../../components";
import { useAuthContext } from "../../context/auth";
import CardsCalor from "./components/CardsCalor";
import TablaCalor from "./components/TablaCalor";
import { useMapaCalorData } from "./hooks/useMapaCalorData";
import styles from "./MapaCalor.module.scss";

const COLOR_PRIORITY = { rojo: 3, amarillo: 2, verde: 1 };

const MapaCalor = () => {
  const { pais } = useAuthContext();
  const { data, loading, maxContactos } = useMapaCalorData(pais);

  const [vistaCards, setVistaCards] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  // Categorías disponibles desde los datos procesados (incluye merges como "AUTOMOTRIZ")
  const categoriasDisponibles = useMemo(() => data.map((cat) => ({ id: cat.id, nombre: cat.nombre })), [data]);

  // Subcategorías disponibles SOLO para la categoría seleccionada
  const subcategoriasDisponibles = useMemo(() => {
    if (!filtroCategoria) return [];
    const cat = data.find((c) => String(c.id) === filtroCategoria);
    if (!cat || !cat.subcategorias) return [];
    return cat.subcategorias.map((s) => s.nombre).sort();
  }, [data, filtroCategoria]);

  const haySubcategorias =
    subcategoriasDisponibles.length > 1 ||
    (subcategoriasDisponibles.length === 1 && subcategoriasDisponibles[0] !== "General");

  // Requiere subcategoría si la categoría tiene subcategorías reales
  const requiereSubcategoria = filtroCategoria && haySubcategorias && !filtroSubcategoria;

  // Solo filtrar si hay categoría (y subcategoría si es requerida)
  const dataFiltrada = useMemo(() => {
    if (!filtroCategoria) return [];
    if (haySubcategorias && !filtroSubcategoria) return [];

    let resultado = data.filter((cat) => String(cat.id) === filtroCategoria);

    if (filtroSubcategoria) {
      resultado = resultado
        .map((cat) => ({
          ...cat,
          subcategorias: cat.subcategorias.filter((sub) => sub.nombre === filtroSubcategoria),
          clientes: cat.clientes.filter((cli) => cli.subcategoria === filtroSubcategoria),
        }))
        .filter((cat) => cat.clientes.length > 0);
    }

    if (filtroEstado) {
      resultado = resultado
        .map((cat) => {
          const subs = (cat.subcategorias || [])
            .map((sub) => {
              const marcasFiltradas = (sub.marcas || [])
                .map((m) => {
                  const prods = m.productos.filter((p) => p.color === filtroEstado);
                  if (prods.length === 0) return null;
                  const worst = prods.reduce(
                    (w, p) => ((COLOR_PRIORITY[p.color] || 0) > (COLOR_PRIORITY[w] || 0) ? p.color : w),
                    "verde"
                  );
                  return { ...m, productos: prods, color: worst };
                })
                .filter(Boolean);
              if (marcasFiltradas.length === 0) return null;
              const allProds = marcasFiltradas.flatMap((m) => m.productos);
              const worst = allProds.reduce(
                (w, p) => ((COLOR_PRIORITY[p.color] || 0) > (COLOR_PRIORITY[w] || 0) ? p.color : w),
                "verde"
              );
              return { ...sub, marcas: marcasFiltradas, clientes: allProds, color: worst };
            })
            .filter(Boolean);
          const allClientes = subs.flatMap((s) => s.clientes);
          if (allClientes.length === 0) return null;
          const worst = allClientes.reduce(
            (w, c) => ((COLOR_PRIORITY[c.color] || 0) > (COLOR_PRIORITY[w] || 0) ? c.color : w),
            "verde"
          );
          return { ...cat, subcategorias: subs, clientes: allClientes, color: worst };
        })
        .filter(Boolean);
    }

    return resultado;
  }, [data, filtroCategoria, filtroSubcategoria, filtroEstado, haySubcategorias]);

  if (loading) return <Loader fullPage />;

  // Mensaje placeholder según estado de selección
  const placeholder = !filtroCategoria
    ? "Selecciona una categoría para ver el mapa de calor"
    : requiereSubcategoria
      ? "Selecciona una subcategoría para ver el detalle"
      : null;

  return (
    <div style={{ padding: 15 }}>
      <Header>
        <select
          className={`${styles.filterSelect} ${filtroCategoria ? styles.activeFilter : ""}`}
          value={filtroCategoria}
          onChange={(e) => {
            setFiltroCategoria(e.target.value);
            setFiltroSubcategoria("");
            setFiltroEstado("");
          }}
        >
          <option value="">Categoría</option>
          {categoriasDisponibles.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>

        <select
          className={`${styles.filterSelect} ${filtroSubcategoria ? styles.activeFilter : ""}`}
          value={filtroSubcategoria}
          onChange={(e) => setFiltroSubcategoria(e.target.value)}
          disabled={!filtroCategoria || !haySubcategorias}
        >
          <option value="">Subcategoría</option>
          {subcategoriasDisponibles.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          className={`${styles.filterSelect} ${filtroEstado ? styles.activeFilter : ""}`}
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
        >
          <option value="">Estado</option>
          <option value="verde">Verde</option>
          <option value="amarillo">Amarillo</option>
          <option value="rojo">Rojo</option>
        </select>

        <button
          className={`${styles.toggleBtn} ${!vistaCards ? styles.active : ""}`}
          onClick={() => setVistaCards(false)}
          type="button"
          title="Vista tabla"
        >
          <HiOutlineViewList size={16} />
        </button>
        <button
          className={`${styles.toggleBtn} ${vistaCards ? styles.active : ""}`}
          onClick={() => setVistaCards(true)}
          type="button"
          title="Vista cards"
        >
          <HiOutlineViewGrid size={16} />
        </button>
      </Header>

      {placeholder ? (
        <div className={styles.placeholder}>{placeholder}</div>
      ) : (
        <div className={styles.wrapper}>
          {vistaCards ? (
            <CardsCalor data={dataFiltrada} />
          ) : (
            <TablaCalor data={dataFiltrada} maxContactos={maxContactos} />
          )}
        </div>
      )}
    </div>
  );
};

export default MapaCalor;
